import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  HelpCircle,
  Heart,
  Lightbulb,
  Calendar,
  ArrowRight,
  Scale,
} from "lucide-react";
import { getTodayLegalStory } from "@/lib/api/daily-legal.functions";
import { StorySocial } from "@/components/site/StorySocial";

export function DailyLegalCard() {
  const fetchToday = useServerFn(getTodayLegalStory);
  const { data } = useQuery({
    queryKey: ["daily-legal-today"],
    queryFn: () => fetchToday(),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });

  const story = data?.story;
  if (!story) return null;

  const publishedAt = new Date(story.publish_at ?? story.publish_date);
  const date = publishedAt.toLocaleString("en-ZA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const shareText = `${story.question}\n\n${story.story}\n\nBrief solution: ${story.solution}\n\nMore daily SA legal stories:`;
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/daily-legal`
      : "/daily-legal";

  return (
    <div className="rounded-2xl border-2 border-gold/40 bg-card p-6 shadow-soft md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Latest legal story
          </p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" /> {date}
          </p>
        </div>
        <span className="rounded-full bg-navy/10 px-3 py-1 text-xs font-medium text-navy">
          {story.category}
        </span>
      </div>

      <div className="mt-5 flex items-start gap-2">
        <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
        <h2 className="font-display text-xl text-navy md:text-2xl">
          {story.question}
        </h2>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Heart className="h-3.5 w-3.5 text-rose-500" /> The story
          </p>
          <p className="text-sm leading-relaxed text-foreground/90">
            {story.story}
          </p>
        </div>
        <div className="rounded-lg border border-gold/30 bg-gold/5 p-4">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-navy">
            <Lightbulb className="h-3.5 w-3.5" /> Brief solution
          </p>
          <p className="text-sm leading-relaxed text-foreground">
            {story.solution}
          </p>
          {story.legislation_refs?.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {story.legislation_refs.slice(0, 3).map((r) => (
                <li key={r} className="flex items-start gap-1.5">
                  <Scale className="mt-0.5 h-3 w-3 shrink-0 text-gold" />
                  <Link
                    to="/legislation"
                    search={{ q: r } as any}
                    className="hover:text-navy hover:underline"
                  >
                    {r}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <StorySocial storyId={story.id} shareText={shareText} shareUrl={shareUrl} />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <p className="text-xs text-muted-foreground">
          A fresh story is published every hour, around the clock.
        </p>
        <div className="flex gap-2">
          <Link
            to="/legislation"
            className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-xs font-medium text-navy hover:border-navy"
          >
            Legislation library
          </Link>
          <Link
            to="/daily-legal"
            className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-2 text-xs font-medium text-navy-foreground hover:bg-navy/90"
          >
            View archive <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
